import { useId, useMemo, useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useSearchPiecesInfinite } from '@/lib/queries/pieces';
import { FormInput } from '@/components/molecules/form-input';
import { Chip } from '@/components/atoms/chip';
import { Micro } from '@/components/atoms/micro';
import { SearchSkeleton } from '@/components/atoms/search-skeleton';
import { NoMatchesFound } from '@/components/molecules/no-matches-found';
import { SearchErrorState } from '@/components/molecules/search-error-state';
import { SearchResultRow } from '@/components/molecules/search-result-row';
import { Button, buttonVariants } from '@/components/atoms/button';
import { Icon } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';
import type { Piece } from '@/lib/types';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const EMPTY_LIBRARY_COPY = 'Search the library to find a tune';
const FILTER_CHIPS: readonly string[] = ['All forms', 'Reels', 'Jigs', 'Airs', 'More'] as const;

export type AddRepertoireSearchPaneProps = {
  selectedPieceId?: string;
  onSelectPiece?: (pieceId: string) => void;
};

export function AddRepertoireSearchPane({
  selectedPieceId,
  onSelectPiece,
}: AddRepertoireSearchPaneProps = {}): ReactElement {
  const [query, setQuery] = useState('');
  const filterLabelId = useId();
  const tailPanelHeadingId = useId();
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const effectiveQuery = debouncedQuery.length >= MIN_SEARCH_LENGTH ? debouncedQuery : '';
  const search = useSearchPiecesInfinite(effectiveQuery);

  const isSearching = effectiveQuery.length >= MIN_SEARCH_LENGTH;
  const results: Piece[] = useMemo(
    () => search.data?.pages.flatMap((page) => page.content) ?? [],
    [search.data],
  );
  const totalElements = search.data?.pages.at(-1)?.totalElements ?? 0;
  const loaded = results.length;
  const hasNextPage = Boolean(search.hasNextPage);
  const isFetchingNextPage = Boolean(search.isFetchingNextPage);

  return (
    <div className="flex flex-1 flex-col">
      <div className="px-6 pb-3 pt-6">
        <FormInput
          id="add-repertoire-search"
          label="Search the library"
          icon="search"
          type="search"
          role="searchbox"
          placeholder="Search pieces — title, composer, alt name…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
        />
        <div className="mt-3">
          <Micro id={filterLabelId} className="block mb-1.5">Filter</Micro>
          <div
            role="group"
            aria-labelledby={filterLabelId}
            className="flex items-center gap-1.5 overflow-x-auto pb-1"
          >
            {FILTER_CHIPS.map((label) => (
              <Chip
                key={label}
                disabled
                aria-disabled="true"
                tabIndex={-1}
                className="opacity-60"
              >
                {label}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      {!isSearching && (
        <div className="flex flex-1 items-center justify-center px-6 pb-6 text-center">
          <p className="max-w-xs text-sm text-[var(--ink-3)]">{EMPTY_LIBRARY_COPY}</p>
        </div>
      )}

      {isSearching && search.isLoading && <SearchSkeleton />}

      {isSearching && search.isError && (
        <SearchErrorState onRetry={() => void search.refetch()} />
      )}

      {isSearching && !search.isLoading && !search.isError && results.length === 0 && (
        <NoMatchesFound query={effectiveQuery} />
      )}

      {isSearching && !search.isLoading && !search.isError && results.length > 0 && (
        <div className="flex flex-1 flex-col">
          <p
            className="px-6 pb-2 pt-1 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]"
            aria-label={`Showing ${loaded} of ${totalElements}`}
          >
            Showing {loaded} of {totalElements}
          </p>
          <ul className="flex flex-col">
            {results.map((piece) => (
              <li key={piece.pieceId}>
                <SearchResultRow
                  piece={piece}
                  selected={piece.pieceId === selectedPieceId}
                  onSelect={() => onSelectPiece?.(piece.pieceId)}
                />
              </li>
            ))}
          </ul>

          {hasNextPage && (
            <div className="flex justify-center px-6 pt-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={isFetchingNextPage}
                aria-busy={isFetchingNextPage || undefined}
                onClick={() => void search.fetchNextPage()}
              >
                {isFetchingNextPage ? 'Loading more…' : 'Load more'}
              </Button>
            </div>
          )}

          <section
            aria-labelledby={tailPanelHeadingId}
            className="m-6 mt-4 rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-soft)] p-5 text-center"
          >
            <h3
              id={tailPanelHeadingId}
              className="font-display text-sm font-semibold tracking-tight text-[var(--ink)]"
            >
              Don&apos;t see it?
            </h3>
            <p className="mt-1 text-xs text-[var(--ink-3)]">
              Our catalog isn&apos;t exhaustive. Add a new piece if your tune is missing.
            </p>
            <div className="mt-3">
              <Link
                to="/library/contribute"
                search={{ returnTo: '/repertoire/add' }}
                className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }))}
              >
                <Icon name="plus" size={13} />
                Contribute a new piece
              </Link>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
